import { useEffect, useState } from 'react';

import { getInsightComments } from 'api/generated/insight-comments/insight-comments';
import type { InsightCommentDto } from 'api/generated/schemas/insightCommentDto';

import { useCurrentUser } from 'hooks/api/use-current-user';

export type InsightComment = InsightCommentDto;

interface UseInsightCommentsArgs {
  insightId: string | number | undefined;
}

interface UseInsightCommentsResult {
  comments: InsightComment[];
  loading: boolean;
  error: unknown;
  refetch: () => Promise<void>;
}

export const useInsightComments = ({
  insightId,
}: UseInsightCommentsArgs): UseInsightCommentsResult => {
  const [comments, setComments] = useState<InsightComment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(null);

  const currentUser = useCurrentUser();
  const userId = currentUser?.data?.oktaUserId;

  const loadComments = async (): Promise<void> => {
    if (!userId || !insightId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const list = await getInsightComments(userId, Number(insightId), {
        page: 0,
        size: 100,
      });

      setComments(list ?? []);
    } catch (err) {
      console.error('Error loading insight comments:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComments();
  }, [userId, insightId]);

  return {
    comments,
    loading,
    error,
    refetch: loadComments,
  };
};
